import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import ar from '@/i18n/locales/ar.json';
import en from '@/i18n/locales/en.json';
import { dirForLang, type Lang } from '@/stores/ui.store';

/**
 * i18next bootstrap (05 §6.1).
 *
 * Arabic is the default and the fallback: an untranslated key must never surface
 * as English on an Arabic screen. Interpolation escaping is off because React
 * already escapes everything it renders.
 *
 * Error messages live under `errors.<ERROR_CODE>` in both locales, keyed by the
 * `ErrorCode` union from `@hadir/shared` — that block is the one M1/M2 grow, and
 * `i18n/errors.test.ts` fails the build if a code has no message.
 */

export const SUPPORTED_LANGS: readonly Lang[] = ['ar', 'en'] as const;
export const DEFAULT_LANG: Lang = 'ar';

export const resources = {
  ar: { translation: ar },
  en: { translation: en },
};

void i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LANG,
  fallbackLng: DEFAULT_LANG,
  supportedLngs: [...SUPPORTED_LANGS],
  defaultNS: 'translation',
  interpolation: { escapeValue: false },
  returnNull: false,
  // The bundles are compiled in, so nothing is async and Suspense is not needed.
  react: { useSuspense: false },
});

/**
 * Apply the language to the document: `lang` and `dir` on <html>, no reload
 * (05 §6.1). Safe to call outside a browser (tests import this module freely).
 */
export function applyDocumentLang(lang: Lang): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('lang', lang);
  root.setAttribute('dir', dirForLang(lang));
}

/** Apply the resolved theme as the `dark` class (05 §6.2, "class" strategy). */
export function applyDocumentTheme(theme: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export function changeLanguage(lang: Lang): void {
  void i18n.changeLanguage(lang);
  applyDocumentLang(lang);
}

export default i18n;
